import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function RequestTypesTab() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/portal/settings");
      setCfg(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setType = (key, field, value) =>
    setCfg((c) => ({ ...c, request_types: { ...c.request_types, [key]: { ...c.request_types[key], [field]: value } } }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/portal/settings", {
        schedule_notice_days: Number(cfg.schedule_notice_days),
        stale_after_days: Number(cfg.stale_after_days),
        request_types: cfg.request_types,
      });
      setCfg(data);
      toast.success("Portal request settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (!cfg) return <p className="text-muted-foreground" data-testid="types-loading">Loading settings…</p>;

  return (
    <div data-testid="request-types-tab">
      <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
        <div>
          <Label className="text-sm font-bold">Notice needed for a schedule change (days)</Label>
          <Input type="number" min={0} max={90} value={cfg.schedule_notice_days}
                 onChange={(e) => setCfg((c) => ({ ...c, schedule_notice_days: e.target.value }))}
                 className="mt-2 h-11 rounded-xl" data-testid="notice-days" />
        </div>
        <div>
          <Label className="text-sm font-bold">Highlight a request after (days)</Label>
          <Input type="number" min={1} max={30} value={cfg.stale_after_days}
                 onChange={(e) => setCfg((c) => ({ ...c, stale_after_days: e.target.value }))}
                 className="mt-2 h-11 rounded-xl" data-testid="stale-days" />
        </div>
      </div>

      <div className="mt-8 bg-white rounded-3xl border border-border overflow-hidden">
        <div className="hidden sm:grid grid-cols-[1fr_7rem_9rem] gap-4 px-5 py-3 bg-slate-50 text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
          <span>Request type</span><span>Families may ask</span><span>A teacher may decide</span>
        </div>
        {cfg.catalogue.map((t) => (
          <div key={t.key}
               className="grid sm:grid-cols-[1fr_7rem_9rem] gap-3 sm:gap-4 px-5 py-4 border-t border-border items-center"
               data-testid={`type-row-${t.key}`}>
            <div>
              <p className="text-sm font-bold">{t.label}</p>
              <p className="text-xs text-muted-foreground">
                {t.group}
                {t.immediate ? " · posts straight to the day" : ""}
                {t.health ? " · goes to health review" : ""}
              </p>
            </div>
            <Switch checked={cfg.request_types[t.key].enabled}
                    onCheckedChange={(v) => setType(t.key, "enabled", v)}
                    data-testid={`type-enabled-${t.key}`} />
            <Switch checked={cfg.request_types[t.key].teacher_can_decide}
                    disabled={!cfg.request_types[t.key].enabled || t.immediate}
                    onCheckedChange={(v) => setType(t.key, "teacher_can_decide", v)}
                    data-testid={`type-teacher-${t.key}`} />
          </div>
        ))}
      </div>

      <Button onClick={save} disabled={busy}
              className="mt-6 h-11 rounded-full font-bold bg-[#3B82F6] hover:bg-[#2563EB]"
              data-testid="save-types">
        {busy ? "Saving…" : "Save settings"}
      </Button>
    </div>
  );
}
